import React, { ReactNode } from 'react';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { ASSISTANT_NAME } from 'src/modules/assistant/constants/widget';

const useStyles = makeStyles((theme: Theme) => ({
    '@keyframes messageReveal': {
        from: { opacity: 0, transform: 'translateY(5px)' },
        to: { opacity: 1, transform: 'translateY(0)' },
    },
    messageRow: {
        display: 'flex',
        marginBottom: theme.spacing(1.5),
    },
    userMessageRow: {
        justifyContent: 'flex-end',
    },
    message: {
        animation: '$messageReveal 180ms ease-out',
        backgroundColor: theme.palette.action.hover,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '16px 16px 16px 5px',
        color: theme.palette.text.primary,
        fontSize: 13,
        lineHeight: 1.55,
        maxWidth: '88%',
        padding: theme.spacing(1.5),
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
    },
    userMessage: {
        backgroundColor: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
        borderRadius: '16px 16px 5px 16px',
        color: theme.palette.primary.contrastText,
    },
    wideMessage: {
        maxWidth: '96%',
        width: '100%',
    },
    assistantIdentity: {
        alignItems: 'center',
        color: theme.palette.text.secondary,
        display: 'flex',
        fontSize: 10,
        fontWeight: 700,
        gap: 6,
        letterSpacing: '0.04em',
        marginBottom: 6,
        textTransform: 'uppercase',
    },
    assistantIdentityDot: {
        background: `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.secondary.main})`,
        borderRadius: '50%',
        height: 7,
        width: 7,
    },
}));

interface AssistantBubbleProps {
    children: ReactNode;
    fromUser?: boolean;
    wide?: boolean;
    role?: string;
    ariaLabel?: string;
}

/**
 * Chat bubble. Assistant bubbles show the assistant identity above the content.
 */
export const AssistantBubble = ({
    children,
    fromUser = false,
    wide = false,
    role,
    ariaLabel,
}: AssistantBubbleProps) => {
    const classes = useStyles();
    return (
        <div
            className={`${classes.messageRow} ${
                fromUser ? classes.userMessageRow : ''
            }`}
        >
            <div
                aria-label={ariaLabel}
                className={`${classes.message} ${
                    fromUser ? classes.userMessage : ''
                } ${wide ? classes.wideMessage : ''}`}
                role={role}
            >
                {!fromUser && (
                    <div className={classes.assistantIdentity}>
                        <span className={classes.assistantIdentityDot} />
                        {ASSISTANT_NAME}
                    </div>
                )}
                {children}
            </div>
        </div>
    );
};
